// Recipe meta forms (create + edit) extracted from ManufacturingModelsPanel
// to keep the orchestrator under the file-size hygiene threshold. Two
// flavors: NewModelForm and EditModelForm — they share UI shape but bind
// to different state slices on the modelTab hook.

import type { MfgRecipeKind } from "@/lib/manufacturing/types";
import { SectionCard } from "@/components/common/SectionCard";
import { ManufacturingModelsDictateMicButton } from "./ManufacturingModelsDictateMicButton";
import { ManufacturingModelsDuplicatePreview } from "./ManufacturingModelsDuplicatePreview";
import { ManufacturingModelsSmartDuplicateAssist } from "./ManufacturingModelsSmartDuplicateAssist";
import type { DuplicateRecipeDraft } from "@/lib/manufacturing/duplicateRecipeDraft";
import type { DuplicateFormPatch } from "@/lib/manufacturing/duplicateRecipeDraftBridge";
import { isDuplicateIdentityUnchanged } from "@/lib/manufacturing/duplicateRecipeDraftBridge";
import { mfgPrimaryBtnClass, adminDangerGhostBtnClass, ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  FieldKind,
  FieldRecipeGroup,
  FieldTagsText,
  FieldText,
} from "./ManufacturingModelsMetaFormFields";
import { ManufacturingModelsImplyRules } from "./ManufacturingModelsImplyRules";
import { parseImpliedVariantTags, type ImpliedVariantRule } from "@/lib/manufacturing/impliedVariantTag";
import { recipeKindHasShelfPrice } from "@/lib/manufacturing/shelfPrice";
import { useState } from "react";

export type NewModelFormProps = {
  newCode: string;
  newName: string;
  newKind: MfgRecipeKind;
  newTagsInput: string;
  newNotes: string;
  newRecipeGroupId: string;
  recipeGroups: ReadonlyArray<{ id: string; code: string; name: string }>;
  saveBusy: boolean;
  setNewCode: (v: string) => void;
  setNewName: (v: string) => void;
  setNewKind: (v: MfgRecipeKind) => void;
  setNewTagsInput: (v: string) => void;
  setNewNotes: (v: string) => void;
  setNewRecipeGroupId: (v: string) => void;
  onCreateRecipe: () => Promise<unknown> | unknown;
  duplicateDraft?: DuplicateRecipeDraft | null;
  onSmartDuplicateApplied?: (patch: DuplicateFormPatch) => void;
  onSmartDuplicateError?: (message: string) => void;
  /** Fires when the dictation mic in the form footer captures a
   *  non-empty transcript. The panel reacts by opening the dictate
   *  review modal pre-seeded with the transcript. When omitted the
   *  mic button is hidden (callers without verbal-auth wiring). */
  onDictationComplete?: (transcript: string) => void;
};

export function NewModelForm(p: NewModelFormProps) {
  const title = p.duplicateDraft ? "New model (duplicate)" : "New model";
  const duplicateIdentityBlocked =
    p.duplicateDraft != null &&
    isDuplicateIdentityUnchanged(p.duplicateDraft, p.newCode, p.newName);
  const canCreate =
    !p.saveBusy &&
    p.newCode.trim().length > 0 &&
    p.newName.trim().length > 0 &&
    !duplicateIdentityBlocked;

  return (
    <SectionCard title={title}>
      <div className="space-y-2">
        <ManufacturingModelsDuplicatePreview draft={p.duplicateDraft ?? null} />
        {p.duplicateDraft && p.onSmartDuplicateApplied ? (
          <ManufacturingModelsSmartDuplicateAssist
            duplicateDraft={p.duplicateDraft}
            code={p.newCode}
            name={p.newName}
            kind={p.newKind}
            tagsInput={p.newTagsInput}
            busy={p.saveBusy}
            onApplied={p.onSmartDuplicateApplied}
            onError={(msg) => p.onSmartDuplicateError?.(msg)}
          />
        ) : null}
        <FieldText
          label="Code"
          value={p.newCode}
          onChange={p.setNewCode}
          hint={
            duplicateIdentityBlocked
              ? "Must differ from the source model code."
              : undefined
          }
        />
        <FieldText
          label="Name"
          value={p.newName}
          onChange={p.setNewName}
          hint={
            duplicateIdentityBlocked
              ? "Must differ from the source model name."
              : undefined
          }
        />
        <FieldKind value={p.newKind} onChange={p.setNewKind} />
        <FieldRecipeGroup
          value={p.newRecipeGroupId}
          onChange={p.setNewRecipeGroupId}
          groups={p.recipeGroups}
        />
        <FieldTagsText
          label="Tags (comma-separated)"
          value={p.newTagsInput}
          onChange={p.setNewTagsInput}
          placeholder="e.g. wood, lofted, 10-wide"
        />
        <FieldText label="Notes" value={p.newNotes} onChange={p.setNewNotes} />
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          onClick={() => void p.onCreateRecipe()}
          disabled={!canCreate}
          title={
            duplicateIdentityBlocked
              ? "Change code and name from the source model before saving."
              : undefined
          }
        >
          {p.saveBusy ? "Saving…" : "Create model"}
        </button>
      </div>

      {p.onDictationComplete ? (
        <div className="mt-4 border-t border-gray-200 pt-3 dark:border-zinc-700">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <div className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
              Or dictate / type the full recipe
            </div>
            <div className="text-[11px] text-gray-500 dark:text-zinc-400">
              AI extracts code, name, kind, and lines.
            </div>
          </div>
          <div className="flex justify-end">
            <ManufacturingModelsDictateMicButton
              onComplete={p.onDictationComplete}
              disabled={p.saveBusy}
            />
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}

export type EditModelFormProps = {
  editCode: string;
  editName: string;
  editKind: MfgRecipeKind;
  editTagsInput: string;
  editNotes: string;
  editRecipeGroupId: string;
  editWholesale: string;
  editMsrp: string;
  sourceTags: readonly string[];
  slotLabels: readonly string[];
  recipeGroups: ReadonlyArray<{ id: string; code: string; name: string }>;
  saveBusy: boolean;
  detailBusy: boolean;
  isActive: boolean;
  canDeactivate: boolean;
  canEditCode: boolean;
  setEditCode: (v: string) => void;
  setEditName: (v: string) => void;
  setEditKind: (v: MfgRecipeKind) => void;
  setEditTagsInput: (v: string) => void;
  setEditNotes: (v: string) => void;
  setEditRecipeGroupId: (v: string) => void;
  setEditWholesale: (v: string) => void;
  setEditMsrp: (v: string) => void;
  onSaveRecipeMeta: (implyRules: readonly ImpliedVariantRule[]) => Promise<unknown> | unknown;
  onClose: () => void;
  onRequestDeactivate?: () => void;
  onRequestPermanentDelete?: () => void;
  canPermanentlyDelete?: boolean;
  /** Opens Restart chooser (wipe / template / AI describe). */
  onRestart?: () => void;
};

export function EditModelForm(p: EditModelFormProps) {
  const [implyRules, setImplyRules] = useState<ImpliedVariantRule[]>(() => parseImpliedVariantTags(p.sourceTags));
  return (
    <SectionCard
      title={p.canEditCode ? "Edit model" : p.editCode}
      rightSlot={
        <button
          type="button"
          className={ghostBtnClass}
          onClick={p.onClose}
          disabled={p.saveBusy || p.detailBusy}
          aria-label="Close model"
        >
          Close
        </button>
      }
    >
      <div className="space-y-2">
        {p.canEditCode ? (
          <FieldText label="Code" value={p.editCode} onChange={p.setEditCode} />
        ) : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-300">
            Model code: <code className="font-medium">{p.editCode}</code>
            <span className="mt-1 block text-[11px] text-gray-500 dark:text-zinc-400">
              Code changes require an org admin or owner.
            </span>
          </div>
        )}
        <FieldText label="Name" value={p.editName} onChange={p.setEditName} />
        <FieldKind value={p.editKind} onChange={p.setEditKind} />
        <FieldRecipeGroup
          value={p.editRecipeGroupId}
          onChange={p.setEditRecipeGroupId}
          groups={p.recipeGroups}
        />
        <FieldTagsText
          label="Tags (comma-separated)"
          value={p.editTagsInput}
          onChange={p.setEditTagsInput}
          placeholder="e.g. wood, lofted, 10-wide"
        />
        <ManufacturingModelsImplyRules
          sourceTags={p.sourceTags}
          slotLabels={p.slotLabels}
          disabled={p.saveBusy || p.detailBusy}
          onChange={setImplyRules}
        />
        {recipeKindHasShelfPrice(p.editKind) ? (
          <>
            <FieldText
              label="Wholesale"
              value={p.editWholesale}
              onChange={p.setEditWholesale}
              placeholder="Current shelf price"
              hint="What dealers pay now. A COGS prediction does not change this. Leave blank if it is not set."
            />
            <FieldText
              label="MSRP"
              value={p.editMsrp}
              onChange={p.setEditMsrp}
              placeholder="Current shelf price"
              hint="What retail is now. A COGS prediction does not change this. Leave blank if it is not set."
            />
          </>
        ) : null}
        <FieldText label="Notes" value={p.editNotes} onChange={p.setEditNotes} />
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              onClick={() => void p.onSaveRecipeMeta(implyRules)}
              disabled={p.saveBusy || p.detailBusy}
            >
              Save meta
            </button>
            {p.canDeactivate && p.isActive && p.onRequestDeactivate ? (
              <button
                type="button"
                className={adminDangerGhostBtnClass}
                onClick={p.onRequestDeactivate}
                disabled={p.saveBusy || p.detailBusy}
              >
                Deactivate model
              </button>
            ) : null}
            {p.canPermanentlyDelete && !p.isActive && p.onRequestPermanentDelete ? (
              <button
                type="button"
                className={adminDangerGhostBtnClass}
                onClick={p.onRequestPermanentDelete}
                disabled={p.saveBusy || p.detailBusy}
              >
                Delete permanently
              </button>
            ) : null}
            {!p.isActive ? (
              <span className="text-xs text-amber-700 dark:text-amber-300">Inactive model</span>
            ) : null}
          </div>
          {p.onRestart ? (
            <button
              type="button"
              className={ghostBtnClass}
              onClick={p.onRestart}
              disabled={p.saveBusy || p.detailBusy}
              title="Wipe, template from another model, or AI restart — keeps code and name"
            >
              Restart…
            </button>
          ) : null}
        </div>
      </div>
    </SectionCard>
  );
}
